import { LearningLayout } from "@jambushrushti/components/learning/LearningLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";

import {
  Users,
  BookOpen,
  TrendingUp,
  Download,
  Calendar,
  Clock,
  CheckCircle,
} from "lucide-react";
import { useAdminCourses } from "@jambushrushti/hooks/useLMS";

const ReportsManage = () => {
  const { data: courses = [] } = useAdminCourses();

  const analytics = {
    totalStudents: "Unavailable",
    activeStudents: "Unavailable",
    totalEnrollments: "Unavailable",
    completionRate: "Unavailable",
    avgProgress: "Unavailable",
    totalLectures: "Unavailable",
    onlineLectures: "Unavailable",
    offlineLectures: "Unavailable",
  };

  const topCourses: {
    name: string;
    enrollments: number;
    completion: number;
  }[] = [];

  const recentActivity: { type: string; message: string; time: string }[] = [];

  return (
    <LearningLayout title="Reports & Analytics">
      <div className="space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {analytics.totalStudents}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Total Students
                  </p>
                </div>
              </div>
              <p className="mt-2 text-xs text-green-600">Unavailable</p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-500/10 p-2 text-blue-600">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {analytics.totalEnrollments}
                  </p>
                  <p className="text-xs text-muted-foreground">Enrollments</p>
                </div>
              </div>
              <p className="mt-2 text-xs text-green-600">Unavailable</p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-green-500/10 p-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {analytics.completionRate}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Completion Rate
                  </p>
                </div>
              </div>
              <p className="mt-2 text-xs text-green-600">Unavailable</p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-orange/10 text-orange rounded-lg p-2">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics.avgProgress}</p>
                  <p className="text-xs text-muted-foreground">Avg Progress</p>
                </div>
              </div>
              <p className="mt-2 text-xs text-green-600">Unavailable</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Top Courses */}
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Top Courses</CardTitle>
                <CardDescription>By enrollment count</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topCourses.length === 0 ? (
                  <p className="col-span-full py-8 text-center text-muted-foreground">
                    No records available.
                  </p>
                ) : (
                  topCourses.map((course, index) => (
                    <div
                      key={course.name}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{course.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {course.enrollments} enrollments
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-16 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${course.completion}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium">
                          {course.completion}%
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest updates and changes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.length === 0 ? (
                  <p className="col-span-full py-8 text-center text-muted-foreground">
                    No records available.
                  </p>
                ) : (
                  recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {activity.message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.time}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Stats Summary */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle>Course Summary</CardTitle>
            <CardDescription>
              Overview of all courses and lectures
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl bg-muted/50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Total Courses</span>
                </div>
                <p className="text-2xl font-bold">{courses.length}</p>
              </div>
              <div className="rounded-xl bg-muted/50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">Total Lectures</span>
                </div>
                <p className="text-2xl font-bold">{analytics.totalLectures}</p>
              </div>
              <div className="rounded-xl bg-muted/50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Online Lectures</span>
                </div>
                <p className="text-2xl font-bold">{analytics.onlineLectures}</p>
              </div>
              <div className="rounded-xl bg-muted/50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Users className="text-orange h-4 w-4" />
                  <span className="text-sm font-medium">Offline Lectures</span>
                </div>
                <p className="text-2xl font-bold">
                  {analytics.offlineLectures}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </LearningLayout>
  );
};

export default ReportsManage;
